import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Send } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const STORES = [["android", "Google Play"], ["ios", "Apple App Store"]];

/** The operator's "put my app on the stores" button. Lands in the platform owner's queue. */
export const AppRequestPanel = () => {
  const [state, setState] = useState(null);
  const [stores, setStores] = useState(["android", "ios"]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/company/app-request");
      setState(data);
      if (data.request) {
        setStores(data.request.stores);
        setNote(data.request.note || "");
      }
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true); setError(""); setSent(false);
    try {
      const { data } = await api.post("/company/app-request", { stores, note });
      setState({ ...state, request: data.request });
      setSent(true);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <Loader2 data-testid="app-request-loading" className="h-5 w-5 animate-spin text-gold" />;
  if (!state.white_label) return null;

  const toggle = (key) =>
    setStores(stores.includes(key) ? stores.filter((s) => s !== key) : [...stores, key]);

  return (
    <div data-testid="app-request-panel" className="rounded-xl border border-gold/30 bg-ink-900/60 p-5">
      <p className="font-display text-lg text-slate-100">Put my app on the stores</p>
      <p className="mt-1 text-sm text-slate-400">
        We build and submit <span className="text-slate-200">{state.app_name}</span> under your name.
        You keep designing it here; publishing is handled for you.
      </p>

      {state.request && (
        <p data-testid="app-request-state" className="mt-3 text-sm text-emerald-400">
          <Check className="mr-1 inline h-4 w-4" />
          {state.request.status_label} — asked on {String(state.request.created_at).slice(0, 10)}
          {state.request.admin_note && (
            <span className="mt-1 block text-slate-400">“{state.request.admin_note}”</span>
          )}
        </p>
      )}
      {!state.icon_ready && (
        <p data-testid="app-request-blocked" className="mt-3 text-sm text-amber-400">
          Upload your square app icon below first — the stores refuse a listing without one.
        </p>
      )}
      {error && <p data-testid="app-request-error" className="mt-3 text-sm text-red-400">{error}</p>}
      {sent && !error && (
        <p data-testid="app-request-sent" className="mt-3 text-sm text-emerald-400">
          Sent. We will come back to you with the listing.
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {STORES.map(([key, label]) => (
          <button key={key} type="button" data-testid={`app-request-store-${key}`}
            onClick={() => toggle(key)}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
              stores.includes(key) ? "border-gold bg-gold/10 text-gold"
                                   : "border-ink-600 text-slate-400 hover:border-gold/40"}`}>
            {label}
          </button>
        ))}
      </div>
      <Textarea data-testid="app-request-note" rows={3} value={note} maxLength={600}
        onChange={(e) => setNote(e.target.value)} placeholder="Anything we should know? (optional)"
        className="mt-3 border-ink-600 bg-ink-800 text-sm text-slate-100 focus-visible:ring-gold" />
      <Button data-testid="app-request-submit" disabled={busy || !stores.length || !state.icon_ready}
        onClick={submit} className="mt-3 bg-gold text-ink-900 hover:bg-gold-light">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
        {state.request ? "Update my request" : "Request my app"}
      </Button>
    </div>
  );
};
